package com.dotdrives.client

import com.dotdrives.model.*
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.exception.PostgrestRestException
import kotlinx.serialization.json.*

/** Uses the current user's bearer and guarded RPCs; never a service key. */
class AdminClient(private val client: SupabaseClient = SupabaseProvider.client) {
    private val workflow = WorkflowClient(client)
    suspend fun entry(): StaffEntry? {
        val role=adminRpc { client.postgrest.rpc("my_staff_role").decodeAs<String?>() } ?: return null
        return try {
            val who=identity()
            StaffEntry(who.role,!who.expired(kotlin.time.Clock.System.now()))
        } catch(e:WorkflowFailure) {
            if(e.code !in setOf("forbidden","unauthorized"))throw e
            StaffEntry(role,false)
        }
    }
    suspend fun identity(): AdminIdentity = try {
        adminRpc { client.postgrest.rpc("admin_identity").decodeAs<AdminIdentity>() }
    } catch (e: WorkflowFailure) {
        if (e.code !in setOf("forbidden", "unauthorized") || client.auth.currentSessionOrNull()?.providerToken.isNullOrBlank()) throw e
        verifyGoogle()
    }
    suspend fun verifyGoogle(): AdminIdentity {
        val token = client.auth.currentSessionOrNull()?.providerToken
            ?: error("Sign in with Google again to verify staff access.")
        val result = workflow.execute(WorkflowRequest("dot-admin", buildJsonObject {
            put("action", "auth"); put("google_access_token", token)
        }))
        return Json { ignoreUnknownKeys = true }.decodeFromJsonElement(result)
    }
    suspend fun read(view: String, filter: JsonObject = buildJsonObject {}): JsonObject = adminRpc {
        client.postgrest.rpc("admin_read", buildJsonObject { put("p_view", view); put("p_filter", filter) }).decodeAs()
    }
    suspend fun write(action: String, orgId: String?, input: JsonObject, reason: String, operationId: String): JsonObject = try {
        client.postgrest.rpc("admin_write", buildJsonObject {
            put("p_action", action); put("p_org", orgId); put("p_input", input)
            put("p_reason", reason); put("p_operation", operationId)
        }).decodeAs<JsonObject>()
    } catch(e:PostgrestRestException) {
        if(e.code=="42501") throw WorkflowFailure("forbidden","Your staff permissions changed.")
        if(e.code=="PGRST301") throw WorkflowFailure("unauthorized","Your staff session ended.")
        if(e.code in setOf("22023","23514","23505","22P02")) throw WorkflowFailure("validation_failed","The details conflict with current records. Check the values, refresh, and try again.")
        throw e
    }
    suspend fun provider(action: String, orgId: String?, input: JsonObject, reason: String, operationId: String): JsonObject =
        workflow.execute(WorkflowRequest("dot-admin", buildJsonObject {
            put("action", action); put("org_id", orgId); put("input", input)
            put("reason", reason); put("operation_id", operationId)
        }))
    suspend fun billingStatus(orgId: String): JsonObject = workflow.execute(WorkflowRequest("dot-admin", buildJsonObject {
        put("action", "billing.status"); put("org_id", orgId)
    }))
    suspend fun reviewRequest(operationId:String,reason:String):JsonObject = adminRpc { client.postgrest.rpc("admin_review_request",buildJsonObject {put("p_operation",operationId);put("p_reason",reason)}).decodeAs() }
    suspend fun orgAccess(orgId: String): OrgAccessState = client.postgrest.rpc("org_access_state", buildJsonObject { put("p_org", orgId) }).decodeAs()
}

private suspend fun <T> adminRpc(block:suspend ()->T):T = try { block() } catch(e:PostgrestRestException) {
    when(e.code) {
        "42501" -> throw WorkflowFailure("forbidden","Your staff permissions changed.")
        "PGRST301" -> throw WorkflowFailure("unauthorized","Your staff session ended.")
        else -> throw e
    }
}
